import {getBucketForId, MembershipBucket} from './SubscriptionsModels';
import {departureLimitByBucket, destinationLimitByBucket} from './MembershipLimits';
import {DateTime} from 'luxon';

export interface User {
    id: number;
    authentication: string;
    cityId: number;
    desktopNotifications: boolean;
    domesticNotifications: boolean;
    email: string;
    emailNotifications: boolean;
    enableDomestic: boolean;
    gender: string;
    hasPaused: boolean;
    lastDomesticRun: number;
    lastSms: number;
    membershipId: number;
    name: string;
    partnerId: string;
    password: string;
    phone: string;
    pushNotifications: boolean;
    pushToken: string;
    referralUrl: string;
    region: string;
    signupDate: string; //ISO string
    smsNotifications: boolean;
    step: number;
    stripeCustomerId: string;
    stripePlanId: string;
    cartId: string;
    token: string;
    // Nuitee loyalty guest id, linked by the backend on every successful
    // hotel booking regardless of tier; null until the first booking.
    nuiteeId: number | null;
}

export class UserModel implements User {
    id!: number;
    authentication!: string;
    cityId!: number;
    desktopNotifications!: boolean;
    domesticNotifications!: boolean;
    email!: string;
    emailNotifications!: boolean;
    enableDomestic!: boolean;
    gender!: string;
    hasPaused!: boolean;
    lastDomesticRun!: number;
    lastSms!: number;
    membershipId!: number;
    name!: string;
    partnerId!: string;
    password!: string;
    phone!: string;
    pushNotifications!: boolean;
    pushToken!: string;
    referralUrl!: string;
    region!: string;
    signupDate!: string; //ISO string
    smsNotifications!: boolean;
    step!: number;
    stripeCustomerId!: string;
    stripePlanId!: string;
    cartId!: string;
    token!: string;
    nuiteeId!: number | null;
    accessStartDate!: Date;

    constructor(params: Partial<UserModel>) {
        Object.assign(this, params);
    }
    get membershipBucket(): MembershipBucket | undefined {
        return getBucketForId?.(this.membershipId);
    }
    get isPremiumPlus(): boolean {
        return this.membershipBucket === MembershipBucket.PremiumPlus;
    }
    get isPremium(): boolean {
        return this.membershipBucket === MembershipBucket.Premium;
    }
    get isPaid(): boolean {
        const b = this.membershipBucket;
        return b === MembershipBucket.Premium || b === MembershipBucket.PremiumPlus;
    }
    get isFree(): boolean {
        return this.membershipBucket === MembershipBucket.Free
    }

    get isOnWaitlist(): boolean {
        if(!this.isFree){
            return false;
        }
        return new Date(this.accessStartDate) > new Date
    }

    get upgradePartnerName(): string {
        const bucket = this.membershipBucket;
        if (bucket === MembershipBucket.Premium) {
            return 'premiumupgradeoptions';
        }
        if (bucket === MembershipBucket.Free) {
            return 'upgradeoptions';
        }
        return '';
    }


    /** Where to send the user to upgrade given current tier */
    get upgradeLink(): string {
        const bucket = this.membershipBucket;
        if (bucket === MembershipBucket.Premium) {
            return '/upgrade/premiumupgradeoptions';
        }
        if (bucket === MembershipBucket.Free) {
            return '/upgrade/upgradeoptions';
        }
        return '';
    }

    get maxDeparturesAllowed(): number {
        const bucket = this.membershipBucket ?? MembershipBucket.Free;
        return departureLimitByBucket[bucket];
    }

    get maxDestinationsAllowed(): number {
        const bucket = this.membershipBucket ?? MembershipBucket.Free;
        return destinationLimitByBucket[bucket];
    }

    isNewUser(thresholdHours: number = 1): boolean {
        const now = DateTime.now();
        //signupDate returned from API as ISO string
        const signupDateTime = DateTime.fromISO(this.signupDate);

        if (!signupDateTime.isValid) {
            return false;
        }

        const hoursSinceSignup = now.diff(signupDateTime, 'hours').hours;
        return hoursSinceSignup <= thresholdHours;
    }
}
export interface UpgradeInfoResponse{
    id: number;
    email: string;
    membershipId: number;
}

const PARTNER_IDS_TO_EXCLUDE_FROM_AB_TEST = [
    'flightdeals1',
    'canadaatest',
    'canadabtest',
    'australiaatest',
    'australiabtest',
    'elevatorbtest1a',
    'elevatorctest2a',
    'brickpixeltest',
    '3daytrial',
    'canada',
    'australia'
]
export function isExcludedFromABVariantTest(partnerId?: string | null): boolean {
    return !!partnerId && PARTNER_IDS_TO_EXCLUDE_FROM_AB_TEST.includes(partnerId);
}
